import { useState, useEffect } from "react";
import PropTypes from "prop-types";

import {
  Container,
  Box,
  Stack,
  Typography,
  TextField,
  Card,
  CardContent,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";

import ArrangementCalculator from "./ArrangementCalculator";

import { useClient } from "../../context/ClientProvider";
import { useArrangement } from "../../context/ArrangementProvider";
import { useSfs } from "../../context/SfsProvider";


const handleSubmit = (event) => {
  event.preventDefault();
};

function ArrangementForm({ errorMessage, setErrorMessage, setValidArrangement }) {

  /* The create repayment form component */

  const { useGetConfig } = useClient();
  const { clientConfig } = useGetConfig();

  const {
    plan,
    setPlan,
    paymentFrequencies = {},
    postChaseForbearance,
  } = useArrangement();

  const { disposableIncome } = useSfs();

  const [ isForbearanceLoading, setIsForbearanceLoading ] = useState(false);
  const [ selectedNumInstalments, setSelectedNumInstalments ] = useState(null);

  async function calculateForbearance() {
    if (selectedNumInstalments === null) {
      setValidArrangement(false);
      setErrorMessage("Please select your number of repayments.");
    } else {
      try {
        // Forbearance request
        const postForbearanceRequest = {
          firstPaymentDate: plan?.nextPaymentDate.format("YYYY-MM-DD"),
          frequency: "monthly",
          term: selectedNumInstalments
        };
        console.log("Post Forbearance Request", postForbearanceRequest);
        setIsForbearanceLoading(true);

        // Forbearance response
        const postForbearanceResponse = await postChaseForbearance(postForbearanceRequest);
        console.log("POST FORBEARANCE RESPONSE", postForbearanceResponse);
        setIsForbearanceLoading(false);

        // Set plan properties
        // Chase forebearance returns payment amount in pence
        const paymentAmountInPounds = postForbearanceResponse.paymentAmount / 100;
        setPlan({
          ...plan,
          numberOfInstalments: selectedNumInstalments,
          paymentsLeft: selectedNumInstalments,
          instalmentAmount: paymentAmountInPounds,
          endDate: plan?.nextPaymentDate.add(selectedNumInstalments - 1, "months")
        });
        if (paymentAmountInPounds <= disposableIncome) {
          setValidArrangement(true);
        } else {
          setValidArrangement(false);
          setErrorMessage("You cannot set up a repayment plan that is higher than your disposable income.");
        };
      } catch(error) {
        console.log("Error in post forbearance request: ", error);
        setValidArrangement(false);
        setIsForbearanceLoading(false);
        setErrorMessage("There was an unexpected error. Please try again.");
      };
    };
  };

  useEffect(() => {
    setErrorMessage(null);
  }, [selectedNumInstalments]);

  return plan ? (
    <Card
      sx={{
        borderRadius: { xs: 0, md: 0 },
        pb: 6,
      }}
    >
      {/* Arrangement calculator section */}
      <Container
        sx={{
          marginY: 2,
        }}
      >
        <ArrangementCalculator setErrorMessage={setErrorMessage} />
      </Container>
      {/* End of arrangement calculator section */}

      <Box component='form' onSubmit={handleSubmit} noValidate>
        <Stack
          sx={{
            paddingX: 1,
          }}
          direction={{
            xs: "column",
            md: "row",
          }}
        >
          {/* Arrangement form left column */}
          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              component='label'
              htmlFor='instalments'
              sx={{ fontWeight: "bold" }}
            >
              Number of repayments
            </Typography>
            <Select
              sx={{ mt: 1, borderRadius: "0px" }}
              id='instalments'
              name='instalments'
              size='small'
              value={selectedNumInstalments}
              onChange={(event) => setSelectedNumInstalments(event.target.value)}
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map(
                (item, idx) => (
                  <MenuItem key={idx} value={item}>
                    {item}
                  </MenuItem>
                )
              )}
            </Select>

            <Typography
              component='label'
              htmlFor='start-date'
              sx={{ mt: { xs: 4, md: 2.5 }, fontWeight: "bold" }}
            >
              Repayment start date
            </Typography>
            <TextField
              margin='dense'
              InputProps={{
                style: {
                  borderRadius: 0,
                  backgroundColor: "#E2E2E2",
                  fontWeight: "bold"
                },
              }}
              fullWidth
              size='small'
              id='start-date'
              name='start-date'
              value={`${plan?.nextPaymentDate.format("DD/MM/YYYY")}`}
              autoFocus
            />

          </CardContent>
          {/* End of arrangement form left column */}

          {/* Arrangement form right column */}
          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              component='label'
              htmlFor='frequency'
              sx={{ fontWeight: "bold"}}
            >
              Repayment frequency
            </Typography>

            <TextField
              margin='dense'
              InputProps={{
                style: {
                  borderRadius: 0,
                  backgroundColor: "#E2E2E2",
                  fontWeight: "bold",
                },
              }}
              fullWidth
              size='small'
              id='frequency'
              name='frequency'
              value={paymentFrequencies[plan?.paymentFrequency]}
              autoFocus
            />

            <Typography
              component='label'
              htmlFor='method'
              sx={{ mt: { xs: 4, md: 2 }, fontWeight: "bold" }}
            >
              Repayment method
            </Typography>
            <TextField
              margin='dense'
              InputProps={{
                style: {
                  borderRadius: 0,
                  backgroundColor: "#E2E2E2",
                  fontWeight: "bold",
                },
              }}
              fullWidth
              size='small'
              id='method'
              name='method'
              value='Bank Transfer'
              autoFocus
            />

          </CardContent>
          {/* End of arrangement form right column */}
        </Stack>

        <Stack
          sx={{ px: 1 }}
          direction={{
            xs: "column",
            md: "row",
          }}
        >
          {/* Error messaging */}
          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ width: "100%" }}>
              {errorMessage && 
                <Alert variant="filled" severity="error">
                  {errorMessage}
                </Alert>
              }
            </Box>
          </CardContent>
          {/* End of error messaging */}

          {/* Calculation button */}
          <CardContent
            sx={{
              textAlign: "left",
              width: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: 'flex',
              }}
            >
              <Button
                type='button'
                onClick={calculateForbearance}
                variant='contained'
                sx={{
                  borderRadius: clientConfig.layout.buttonBorderRadius,
                  width: { xs: "100%" },
                  backgroundColor: clientConfig.layout.primaryButtonBackgroundColour,
                  color: clientConfig.layout.primaryButtonColour,
                  "&:hover": {
                    backgroundColor: clientConfig.layout.hoverPrimaryButtonBackgroundColour,
                    color: clientConfig.layout.hoverPrimaryButtonColour,
                  }
                }}
              >
                Calculate
              </Button>
            </Box>
          </CardContent>
          {/* End of calculation button */}
        </Stack>
        {isForbearanceLoading &&
          <Box id='forbearance-loading-spinner' sx={{ textAlign: "center" }}>
            <CircularProgress
              size={24}
              sx = {{
                position: "absolute",
              }}
            />
          </Box>
        }
      </Box>
    </Card>
  ) : null;
};

ArrangementForm.propTypes = {
  errorMessage: PropTypes.string,
  setErrorMessage: PropTypes.func,
  setValidArrangement: PropTypes.func,
};

export default ArrangementForm;
